import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

import { useTranslation } from 'react-i18next'

import type { PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist'

type Props = {
    blob: Blob
    zoom: number
    scrollRef: RefObject<HTMLDivElement>
    onReady: (width: number, pages: number) => void
    onPage: (page: number) => void
    onError: () => void
}

export function PDFView({ blob, zoom, scrollRef, onReady, onPage, onError }: Props) {
    const [pages, setPages] = useState<PDFPageProxy[]>([])
    useEffect(() => {
        let cancelled = false
        let task: PDFDocumentLoadingTask | undefined
        let pdf: PDFDocumentProxy | undefined
        const load = async () => {
            try {
                const [buffer, pdfjs] = await Promise.all([
                    blob.arrayBuffer(),
                    import(/* webpackChunkName: "file-preview-pdfjs" */ 'pdfjs-dist/webpack.mjs'),
                ])
                if (cancelled) return
                task = pdfjs.getDocument({ data: buffer })
                pdf = await task.promise
                if (cancelled) return
                const loaded = await Promise.all(Array.from({ length: pdf.numPages }, (_, i) => pdf!.getPage(i + 1)))
                if (cancelled) return
                setPages(loaded)
                onReady(Math.max(...loaded.map((page) => page.getViewport({ scale: 1 }).width)), loaded.length)
            } catch {
                if (!cancelled) onError()
            }
        }
        void load()
        return () => {
            cancelled = true
            if (task) void task.destroy().catch(() => undefined)
        }
    }, [blob, onReady, onError])

    const updatePage = () => {
        const container = scrollRef.current
        if (!container) return
        const bounds = container.getBoundingClientRect()
        const nodes = Array.from(container.querySelectorAll<HTMLElement>('[data-page]'))
        let selected = 1
        let largestRatio = 0
        nodes.forEach((node) => {
            const rect = node.getBoundingClientRect()
            const ratio = (Math.min(rect.bottom, bounds.bottom) - Math.max(rect.top, bounds.top)) / rect.height
            if (ratio > largestRatio) {
                largestRatio = ratio
                selected = Number(node.dataset.page)
            }
        })
        onPage(selected)
    }

    return (
        <div className="CandidateFilePreview__pdf" ref={scrollRef} onScroll={updatePage}>
            {pages.map((page) => (
                <PDFPage
                    key={`${page.pageNumber}-${zoom}`}
                    page={page}
                    zoom={zoom}
                    root={scrollRef}
                    onError={onError}
                />
            ))}
        </div>
    )
}

function PDFPage({
    page,
    zoom,
    root,
    onError,
}: {
    page: PDFPageProxy
    zoom: number
    root: RefObject<HTMLDivElement>
    onError: () => void
}) {
    const { t } = useTranslation()
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const viewport = page.getViewport({ scale: zoom / 100 })

    useEffect(() => {
        let cancelled = false
        let task: RenderTask | undefined
        let rendered = false
        const canvas = canvasRef.current
        const render = () => {
            if (!canvas || rendered || cancelled) return
            rendered = true
            const context = canvas.getContext('2d')
            if (!context) {
                onError()
                return
            }
            // Cap backing resolution to avoid huge allocations at 200% on retina displays.
            const outputScale = Math.min(window.devicePixelRatio || 1, 2)
            const view = page.getViewport({ scale: zoom / 100 })
            canvas.width = Math.ceil(view.width * outputScale)
            canvas.height = Math.ceil(view.height * outputScale)
            try {
                task = page.render({
                    canvasContext: context,
                    viewport: view,
                    transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
                })
                void task.promise.catch(() => {
                    if (!cancelled) onError()
                })
            } catch {
                if (!cancelled) onError()
            }
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) render()
            },
            { root: root.current, rootMargin: '200% 0px' }
        )
        if (wrapperRef.current) observer.observe(wrapperRef.current)
        return () => {
            cancelled = true
            observer.disconnect()
            task?.cancel()
            // Release offscreen canvas memory when zooming or closing the viewer.
            if (canvas) canvas.width = canvas.height = 0
        }
    }, [page, zoom, root, onError])

    return (
        <div
            ref={wrapperRef}
            className="CandidateFilePreview__pdf-page"
            data-page={page.pageNumber}
            style={{ width: viewport.width, height: viewport.height }}
        >
            <canvas
                ref={canvasRef}
                style={{ width: viewport.width, height: viewport.height }}
                role="img"
                aria-label={`${String(t('7504', 'Page') ?? 'Page')} ${page.pageNumber}`}
            />
        </div>
    )
}
